import { FC, useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography } from '@kizen/kds/Typography';
import { Responses, FloatingResponsesWrapper } from './styles';

export type FloatingResponseData = {
  options: string[];
  selectType: 'single_select' | 'multi_select';
};

type AutoResponsesProps = {
  isMobile: boolean;
  isMessageResponseLoading: boolean;
  floatingResponseData: FloatingResponseData;
  onSelect: (id: string) => void;
  messageHistoryWrapperRef: React.RefObject<HTMLDivElement>;
  setFocusTextArea: (focus: boolean) => void;
  selectedResponses: { id: string; value: string }[];
  chatSidePadding: string;
};

type AutoResponseButtonProps = {
  key: string;
  onClick: (answer: string) => void;
  selected: boolean;
  answer: string;
  isMobile: boolean;
  setFocusTextArea: (focus: boolean) => void;
  messageHistoryWrapperRef: React.RefObject<HTMLDivElement>;
  t: any;
};

const OTHER_OPTION = (t: any) => t('Other');

const AutoResponseButton: FC<AutoResponseButtonProps> = ({
  onClick,
  answer,
  isMobile,
  setFocusTextArea,
  messageHistoryWrapperRef,
  selected,
  t,
}) => {
  return (
    <button
      disabled={selected}
      onClick={(event) => {
        event.preventDefault();
        if (answer !== OTHER_OPTION(t)) {
          onClick(answer);
          messageHistoryWrapperRef.current?.scrollTo({
            top: messageHistoryWrapperRef.current.scrollHeight,
            behavior: 'smooth',
          });
        } else {
          setFocusTextArea(true);
        }
      }}
    >
      <Typography size={isMobile ? 'sm' : 'sm'} enableTooltip={false}>
        {answer}
      </Typography>
    </button>
  );
};

export const FloatingResponses: FC<AutoResponsesProps> = ({
  isMobile,
  isMessageResponseLoading,
  floatingResponseData,
  onSelect,
  messageHistoryWrapperRef,
  setFocusTextArea,
  selectedResponses,
  chatSidePadding,
}) => {
  const { t } = useTranslation();
  const isOdd = floatingResponseData.options.length % 2 !== 0;
  const autoResponseWrapperRef = useRef<HTMLDivElement>(null);
  const [floatingTop, setFloatingTop] = useState('0px');

  // ensure floating auto responses are always at the bottom of the chat
  useEffect(() => {
    const currentWrapperRef = autoResponseWrapperRef.current;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.target === currentWrapperRef) {
          const height = entry.contentRect.height;
          const offset = isMobile ? 100 : 10;
          setFloatingTop(`-${height + offset}px`);
        }
      }
    });

    if (currentWrapperRef) {
      observer.observe(currentWrapperRef);
    }

    return () => {
      if (currentWrapperRef) {
        observer.unobserve(currentWrapperRef);
      }
    };
  }, [isMobile]);

  return (
    <FloatingResponsesWrapper>
      <Responses
        isMobile={isMobile}
        isOdd={isOdd}
        sidePadding={chatSidePadding}
        style={{ top: floatingTop }}
        ref={autoResponseWrapperRef}
      >
        {!isMessageResponseLoading && floatingResponseData.options.length > 0
          ? floatingResponseData.options.map((answer, idx) => (
              <AutoResponseButton
                key={`auto-response-${idx}`}
                onClick={onSelect}
                answer={answer}
                isMobile={isMobile}
                setFocusTextArea={setFocusTextArea}
                messageHistoryWrapperRef={messageHistoryWrapperRef}
                selected={Boolean(
                  selectedResponses?.find((r) => r.value === answer)
                )}
                t={t}
              />
            ))
          : null}
      </Responses>
    </FloatingResponsesWrapper>
  );
};
